import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  Button,
  InputAdornment,
  TextField,
  Typography,
} from '@material-ui/core';
import { Progress } from '@backstage/core-components';
import LockIcon from '@material-ui/icons/LockOutlined';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import SearchIcon from '@material-ui/icons/Search';
import { brand } from '../../themes/howdenTheme';
import {
  AVAILABILITY_LABEL,
  CAPABILITIES,
  CAPABILITY_AREAS,
  SERVICES_ROOT,
  serviceHref,
} from '../../modules/capabilities/capabilities';
import type { DataSphereCapability } from '../../modules/capabilities/capabilities';

export { SERVICES_ROOT, serviceHref };

const SHORT: Record<string, string> = {
  'roadmap-2026': '2026',
  'roadmap-2027': '2027',
};

const useStyles = makeStyles(theme => ({
  root: {
    maxWidth: 1680,
    margin: '0 auto',
    padding: theme.spacing(3, 5, 5),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 2, 4),
    },
  },
  bar: {
    marginBottom: theme.spacing(4),
    borderBottom: `1px solid ${brand.border}`,
  },
  barTop: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  breadcrumb: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    listStyle: 'none',
    margin: 0,
    padding: 0,
    fontSize: 14,
    color: brand.muted,
    '& a': { color: brand.primary, fontWeight: 600 },
  },
  crumbIcon: { fontSize: 18, color: brand.muted },
  switcher: {
    width: 360,
    maxWidth: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: 0,
      backgroundColor: brand.surface,
    },
  },
  option: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    width: '100%',
  },
  optionTag: { fontSize: 12, color: brand.muted },
  // The area's services as a row of tabs. It scrolls sideways instead of
  // wrapping, so the bar keeps one height on every service.
  strip: {
    display: 'flex',
    gap: theme.spacing(0.5),
    overflowX: 'auto',
    listStyle: 'none',
    margin: 0,
    padding: 0,
    scrollbarWidth: 'thin',
  },
  tab: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 2),
    borderBottom: '3px solid transparent',
    color: brand.ink,
    fontSize: 14,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    textDecoration: 'none',
    '&:hover': { color: brand.primary, backgroundColor: brand.subtle },
  },
  tabCurrent: {
    color: brand.primary,
    borderBottomColor: brand.primary,
  },
  tabPlanned: { color: brand.muted, fontWeight: 400 },
  tag: {
    fontSize: 11,
    fontWeight: 600,
    padding: theme.spacing(0, 0.75),
    border: `1px solid ${brand.muted}`,
    color: brand.muted,
  },
  main: { minWidth: 0 },
  denied: {
    maxWidth: 720,
    padding: theme.spacing(4),
    border: `1px solid ${brand.border}`,
    borderTop: `4px solid ${brand.primary}`,
    '& h2': { color: brand.ink, margin: theme.spacing(1, 0, 2) },
    '& p': { color: brand.ink, marginBottom: theme.spacing(3) },
  },
  deniedIcon: { color: brand.primary },
}));

type Props = {
  /** The capability being shown, if it is on the map. */
  current?: DataSphereCapability;
  /** Title for the breadcrumb when the service is not on the map. */
  fallbackTitle?: string;
  /**
   * The template behind the form, when this frame wraps the wizard. The form
   * only renders once we know the persona can run it.
   */
  templateName?: string;
  children: ReactNode;
};

/**
 * The frame around every service: a breadcrumb, a search box to jump to any
 * other service, and the services of the current area as tabs. The form or
 * placeholder gets the full width below.
 */
export const ServiceShell = ({
  current,
  fallbackTitle,
  templateName,
  children,
}: Props) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const catalogApi = useApi(catalogApiRef);
  const showPlanned =
    useApi(configApiRef).getOptionalBoolean('datasphere.showPlannedServices') ??
    false;

  // Only offer live services the persona can run: the backend permission
  // policy returns just those templates.
  const [runnable, setRunnable] = useState<Set<string>>();
  useEffect(() => {
    let cancelled = false;
    catalogApi
      .getEntities({ filter: { kind: 'Template' }, fields: ['metadata.name'] })
      .then(response => {
        if (!cancelled)
          setRunnable(new Set(response.items.map(e => e.metadata.name)));
      })
      .catch(() => {
        if (!cancelled) setRunnable(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi]);

  const visible = useMemo(
    () =>
      CAPABILITIES.filter(c => c.area !== 'insights').filter(c =>
        c.availability === 'live'
          ? !!c.templateName && (runnable?.has(c.templateName) ?? false)
          : showPlanned,
      ),
    [runnable, showPlanned],
  );
  const area = CAPABILITY_AREAS.find(a => a.id === current?.area);
  const inArea = visible.filter(c => c.area === current?.area);
  const areaTitle = (id: string) =>
    CAPABILITY_AREAS.find(a => a.id === id)?.title ?? '';

  return (
    <div className={classes.root}>
      <div className={classes.bar}>
        <div className={classes.barTop}>
          <nav aria-label="Breadcrumb">
            <ol className={classes.breadcrumb}>
              <li>
                <RouterLink to={SERVICES_ROOT}>Services</RouterLink>
              </li>
              {area ? (
                <>
                  <ChevronRightIcon className={classes.crumbIcon} aria-hidden />
                  <li>
                    <RouterLink to={`${SERVICES_ROOT}#services-${area.id}`}>
                      {area.title}
                    </RouterLink>
                  </li>
                </>
              ) : null}
              <ChevronRightIcon className={classes.crumbIcon} aria-hidden />
              <li aria-current="page">{current?.title ?? fallbackTitle}</li>
            </ol>
          </nav>
          <Autocomplete
            className={classes.switcher}
            options={visible}
            groupBy={option => areaTitle(option.area)}
            getOptionLabel={option => option.title}
            renderOption={option => (
              <span className={classes.option}>
                <span>{option.title}</span>
                {option.availability !== 'live' ? (
                  <span className={classes.optionTag}>
                    {AVAILABILITY_LABEL[option.availability]}
                  </span>
                ) : null}
              </span>
            )}
            onChange={(_, value) => {
              if (value) navigate(serviceHref(value));
            }}
            value={null}
            blurOnSelect
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                placeholder="Switch to another service"
                inputProps={{
                  ...params.inputProps,
                  'aria-label': 'Switch to another service',
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        fontSize="small"
                        style={{ color: brand.muted }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </div>
        {inArea.length > 1 ? (
          <nav aria-label={`${area?.title ?? 'Area'} services`}>
            <ul className={classes.strip}>
              {inArea.map(item => {
                const isCurrent = item.id === current?.id;
                const planned = item.availability !== 'live';
                return (
                  <li key={item.id}>
                    <RouterLink
                      to={serviceHref(item)}
                      aria-current={isCurrent ? 'page' : undefined}
                      className={[
                        classes.tab,
                        planned ? classes.tabPlanned : '',
                        isCurrent ? classes.tabCurrent : '',
                      ].join(' ')}
                    >
                      {item.title}
                      {planned ? (
                        <span
                          className={classes.tag}
                          title={AVAILABILITY_LABEL[item.availability]}
                        >
                          {SHORT[item.availability]}
                        </span>
                      ) : null}
                    </RouterLink>
                  </li>
                );
              })}
            </ul>
          </nav>
        ) : null}
      </div>
      <main className={classes.main}>
        {(() => {
          if (!templateName) return children;
          // The stock wizard sends the user back to the list with a 404 toast
          // when the template is not theirs to run, so do not mount it until
          // the catalog has said which templates this persona can see.
          if (!runnable) return <Progress />;
          if (runnable.has(templateName)) return children;
          return (
            <section
              className={classes.denied}
              aria-labelledby="service-denied"
            >
              <LockIcon className={classes.deniedIcon} aria-hidden />
              <Typography id="service-denied" variant="h5" component="h2">
                This service is not available to you
              </Typography>
              <Typography variant="body1">
                Your DataSphere role does not include this service. If you need
                it, ask your spoke owner or the Platform Engineering team for
                the right role.
              </Typography>
              <Button
                component={RouterLink}
                to={SERVICES_ROOT}
                variant="contained"
                color="primary"
              >
                Back to services
              </Button>
            </section>
          );
        })()}
      </main>
    </div>
  );
};
